import { Box, EvaneosThemeProvider, paletteColors } from '@evaneos/b2b-ui';
import { lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { hardenDomForTranslation } from '@shared/bootstrap/hardenDomForTranslation';
import { installChunkReloadOnPreloadError } from '@shared/bootstrap/installChunkReloadOnPreloadError';
import { HOME_BASENAME, RM_BASENAME } from '@shared/routing/basenames';
import { Loader } from '@widgets/Loader';
import { setTheme, toastConfig } from '@shared/providers/theme';

// CSS globaux — montés une seule fois dans le chunk initial, partagés par toutes les apps.
import '@shared/design-system/tokens.global.css';
import '@shared/design-system/z-index.global.css';
import '@shared/styles/reset.global.css';
import '@widgets/Form/DayPicker.global.css';
import '@shared/styles/third-party-libraries.global.css';
import '@evaneos/b2b-ui/index.css';
// to delete when the new brand colors are in B2B-UI
import '@shared/styles/colors-rebranding.global.css';

// Chaque app porte sa propre stack de providers et ses propres side-effects. Le chargement
// lazy fait émettre à Vite un chunk distinct par app : les deps lourdes (Redux, sagas,
// React Query, Amplitude, Datadog…) et les side-effects de RM ne sont tirés qu'à la demande.
const HomeApp = lazy(() => import('./apps/home/HomeApp'));
const RequestManagerApp = lazy(() => import('./apps/requestManager/RequestManagerApp'));

// Vite expose le chemin de base du build dans import.meta.env.BASE_URL. En preview PR il vaut le
// sous-chemin par-PR (/ets-request-manager-app/pr-<N>/) → c'est le basename du routeur, qui préfixe
// toutes les routes et navigations. En prod/staging BASE_URL est l'URL CDN absolue des assets (pas
// un chemin de route) ; on retombe alors sur '/' (app montée à la racine, segments /request-manager).
const buildBase = import.meta.env.BASE_URL;
const ROUTER_BASENAME = buildBase.startsWith('http') ? '/' : buildBase.replace(/\/$/, '') || '/';

// Affiché le temps que le chunk de l'app (lazy) soit téléchargé puis exécuté. Vit dans le
// chunk initial (le shell) : il doit pouvoir s'afficher avant que l'app cible ne soit chargée.
const AppLoadingFallback = () => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            backgroundColor: paletteColors.green[700],
        }}
    >
        <Loader color={paletteColors.neutral[0]} />
    </Box>
);

const App = () => (
    <EvaneosThemeProvider rootHtmlFontSize={10} setTheme={setTheme} toastConfig={toastConfig}>
        <BrowserRouter basename={ROUTER_BASENAME}>
            <Suspense fallback={<AppLoadingFallback />}>
                <Routes>
                    <Route path={`${HOME_BASENAME}/*`} element={<HomeApp />} />
                    <Route path={`${RM_BASENAME}/*`} element={<RequestManagerApp />} />
                    {/* Tout chemin hors des apps enregistrées (racine "/", hôte non configuré…)
                        retombe sur le Request Manager. */}
                    <Route path="*" element={<Navigate to={`${RM_BASENAME}/`} replace />} />
                </Routes>
            </Suspense>
        </BrowserRouter>
    </EvaneosThemeProvider>
);

const renderApp = () => {
    const root = createRoot(document.getElementById('app') as HTMLElement);
    root.render(<App />);
};

// Durcissement runtime à brancher avant le 1er rendu : tolérance aux extensions de traduction
// (évite les crashes removeChild, 1re cause de crash front) et reload auto sur chunk périmé après
// déploiement (2e cause).
hardenDomForTranslation();
installChunkReloadOnPreloadError();

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', renderApp);
} else {
    renderApp();
}
